import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoUrl from '../assets/partners/logo-vertical.png';
import heroUrl from '../assets/partners/repair-shop-hero.png';

const benefits = [
  'Receive qualified repair inquiries',
  'Get customer issue summaries before arrival',
  'Build digital service history for customers',
];

const pilotNotes = [
  'More repair-ready customers',
  'Faster customer intake',
  'Digital service records',
];

const nextSteps = [
  'We review your shop details.',
  'We confirm your preferred contact method.',
  'We share onboarding details for the early partner program.',
];

const emailPattern = String.raw`[^\s@]+@[^\s@]+\.[^\s@]+`;
const countryCodes = [
  { code: '+1', label: 'US +1', min: 10, max: 10 },
  { code: '+91', label: 'IN +91', min: 10, max: 10 },
  { code: '+44', label: 'UK +44', min: 10, max: 10 },
  { code: '+61', label: 'AU +61', min: 9, max: 9 },
  { code: '+971', label: 'UAE +971', min: 8, max: 9 },
];

function phoneHasValidDigits(countryCode, value) {
  const digits = value.replace(/\D/g, '');
  const country = countryCodes.find((item) => item.code === countryCode) ?? countryCodes[0];

  return digits.length >= country.min && digits.length <= country.max;
}

function getSignupSource(search) {
  const params = new URLSearchParams(search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmContent = params.get('utm_content');

  if (utmSource === 'partner' && utmMedium === 'qr' && utmCampaign === 'repair_shop_outreach' && utmContent) {
    const cleanContent = utmContent.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 80);
    if (cleanContent) {
      return `partner_qr:${cleanContent}`;
    }
  }

  return 'repair_partners_signup';
}

function BackgroundShell({ children, confirmation = false }) {
  return (
    <main
      className="min-h-dvh bg-cover bg-center font-inter text-ink md:grid md:grid-cols-[minmax(0,1fr)_minmax(360px,520px)] lg:grid-cols-[minmax(0,1fr)_clamp(440px,25vw,490px)]"
      style={{
        backgroundImage: confirmation
          ? `linear-gradient(90deg, rgba(17,17,17,.84), rgba(17,17,17,.32) 58%, rgba(241,241,241,.92)), url("${heroUrl}")`
          : `linear-gradient(90deg, rgba(17,17,17,.84), rgba(17,17,17,.34) 58%, rgba(17,17,17,.08)), url("${heroUrl}")`,
      }}
    >
      {children}
    </main>
  );
}

function Hero({ confirmation = false }) {
  return (
    <section
      className="flex min-h-[52vh] flex-col justify-between px-4 py-5 text-white sm:px-5 sm:py-6 md:min-h-dvh md:px-8 lg:px-[clamp(24px,5vw,72px)]"
      aria-label={confirmation ? 'CarFido Partner Network confirmation' : 'CarFido Partner Network'}
    >
      <Link to="/home" className="w-fit" aria-label="Go to CarFido home">
        <img
          className="block w-[158px] md:w-[clamp(126px,12vw,176px)]"
          src={logoUrl}
          alt="CarFido"
        />
      </Link>

      <div className="max-w-[660px] py-9 sm:py-12 md:py-10">
        <p className="mb-2.5 text-xs font-extrabold uppercase tracking-[.08em] text-[#D81F26] sm:text-[.82rem]">
          Partner Network
        </p>
        <h1 className="max-w-[620px] text-[2.35rem] font-extrabold leading-[1.03] tracking-normal sm:text-[clamp(2.5rem,10vw,4rem)] md:text-[clamp(2.5rem,5.2vw,5rem)] md:leading-[.96]">
          {confirmation
            ? 'Thanks for joining the CarFido Partner Network'
            : 'Become a CarFido Repair Partner'}
        </h1>
        <p className="mt-4 max-w-[560px] text-[.98rem] text-[#F2F2F2] sm:text-base md:mt-5 md:text-[clamp(1rem,1.5vw,1.2rem)]">
          {confirmation
            ? 'Your signup was received. The CarFido team will review your shop details and follow up with onboarding next steps.'
            : 'Join the CarFido Partner Network to receive repair-ready customer leads, AI-assisted intake reports, and access to upcoming shop tools.'}
        </p>

        {!confirmation && (
          <>
            <ul className="mt-4 grid max-w-[560px] gap-2 text-[.92rem] text-[#F2F2F2] sm:text-[.98rem]" aria-label="Partner benefits">
              {benefits.map((benefit) => (
                <li className="flex items-start gap-2.5" key={benefit}>
                  <span className="mt-2 block size-2 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <ul className="mt-5 grid gap-2 sm:flex sm:flex-wrap" aria-label="Pilot highlights">
              {pilotNotes.map((note) => (
                <li
                  className="border border-white/35 bg-white/10 px-3 py-2 text-[.84rem] font-bold backdrop-blur-md sm:text-sm"
                  key={note}
                >
                  {note}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <p className="text-[.84rem] text-white/80 md:text-[.92rem]">carfido.com/partner</p>
    </section>
  );
}

function SignupForm({ source }) {
  function handleSubmit(event) {
    const form = event.currentTarget;
    const phone = form.elements.phone;
    const countryCode = form.elements.country_code;

    phone.setCustomValidity('');
    if (!phoneHasValidDigits(countryCode.value, phone.value)) {
      phone.setCustomValidity('Enter a valid phone number for the selected country code.');
    }

    if (!form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
    }
  }

  function handlePhoneInput(event) {
    event.currentTarget.setCustomValidity('');
  }

  return (
    <section className="bg-[#f1f1f1] p-0 md:flex md:min-h-dvh md:items-center md:bg-[#f1f1f1]/95 md:p-6" aria-label="Signup form">
      <form
        className="mx-auto w-full max-w-none bg-white px-4 py-7 shadow-none md:max-w-[432px] md:rounded-lg md:border md:border-line/90 md:px-8 md:py-6 md:shadow-panel"
        name="dash-partner-network-signup"
        method="post"
        action="/api/repair-shop-pilot-signups"
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="form-name" value="dash-partner-network-signup" />
        <input type="hidden" name="source" value={source} />
        <p className="hidden">
          <label>Do not fill this out <input name="bot-field" /></label>
        </p>

        <div className="mb-5 md:mb-3">
          <h2 className="m-0 text-2xl font-extrabold leading-tight tracking-normal md:text-[clamp(1.55rem,1.8vw,1.86rem)]">
            Join the CarFido Partner Network
          </h2>
          <p className="mt-1.5 text-muted">Share your shop details to request early partner access.</p>
        </div>

        <TextField id="shop-name" name="shop_name" label="Shop Name" autoComplete="organization" maxLength="120" placeholder="Your repair shop" required />
        <TextField
          id="representative-name"
          name="representative_name"
          label="Representative Name"
          autoComplete="name"
          maxLength="100"
          pattern="[A-Za-z][A-Za-z .'-]*"
          placeholder="First and last name"
          title="Use letters, spaces, periods, apostrophes, and hyphens only."
          required
        />
        <TextField
          id="email"
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          maxLength="254"
          pattern={emailPattern}
          placeholder="name@shop.com"
          title="Enter a basic email address, like name@shop.com."
          required
        />
        <PhoneField onInput={handlePhoneInput} />
        <TextField id="location" name="location_city" label="Location / City" optional autoComplete="address-level2" maxLength="100" placeholder="City, State" />

        <button
          className="mt-5 min-h-[52px] w-full rounded-md bg-brand px-4 py-3 font-extrabold text-white transition hover:bg-brand-dark focus-visible:bg-brand-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-brand/20 md:mt-3 md:min-h-11"
          type="submit"
        >
          Become a Partner
        </button>
        <p className="mt-2.5 text-[.82rem] text-muted md:text-xs">
          No fees. No commitment. Early pilot participants will receive priority onboarding and direct feedback access.
        </p>
        <p className="mt-1 text-[.82rem] font-bold text-muted md:text-xs">
          A CarFido team member will contact you within 1-2 business days.
        </p>
      </form>
    </section>
  );
}

function PhoneField({ onInput }) {
  return (
    <div className="mt-3 grid gap-1.5 md:mt-2">
      <label className="text-[.84rem] font-extrabold" htmlFor="phone">
        Phone Number
      </label>
      <div className="grid grid-cols-[112px_minmax(0,1fr)] overflow-hidden rounded-md border border-line bg-field transition focus-within:border-brand focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(216,31,38,.13)] sm:grid-cols-[124px_minmax(0,1fr)]">
        <select
          className="min-h-[52px] w-full border-0 border-r border-line bg-transparent px-3 text-sm font-bold text-ink outline-none md:min-h-[42px]"
          name="country_code"
          defaultValue="+1"
          aria-label="Country code"
          onChange={(event) => {
            event.currentTarget.form?.elements.phone?.setCustomValidity('');
          }}
        >
          {countryCodes.map((country) => (
            <option key={country.code} value={country.code}>
              {country.label}
            </option>
          ))}
        </select>
        <input
          className="min-h-[52px] w-full min-w-0 border-0 bg-transparent px-3.5 py-2.5 text-base text-ink outline-none md:min-h-[42px]"
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          minLength="8"
          maxLength="18"
          placeholder="Phone number"
          title="Enter a valid phone number for the selected country code."
          onInput={onInput}
          required
        />
      </div>
      <p className="m-0 text-xs text-muted">Select the country code, then enter the shop or representative phone number.</p>
    </div>
  );
}

function TextField({ label, optional = false, hint, className = '', ...props }) {
  return (
    <div className={`mt-3 grid gap-1.5 md:mt-2 ${className}`}>
      <label className="text-[.84rem] font-extrabold" htmlFor={props.id}>
        {label} {optional && <span className="font-bold text-muted">optional</span>}
      </label>
      <input
        className="min-h-[52px] w-full rounded-md border border-line bg-field px-3.5 py-2.5 text-base text-ink outline-none transition focus:border-brand focus:bg-white focus:shadow-[0_0_0_4px_rgba(216,31,38,.13)] md:min-h-[42px]"
        {...props}
      />
      {hint && <p className="m-0 text-xs text-muted">{hint}</p>}
    </div>
  );
}

function ThanksPanel() {
  return (
    <section className="bg-[#f1f1f1] p-0 md:flex md:min-h-dvh md:items-center md:bg-[#f1f1f1]/95 md:p-9" aria-label="Signup confirmation details">
      <div className="mx-auto min-h-[52vh] w-full max-w-none bg-white px-5 py-8 md:min-h-0 md:max-w-[430px] md:rounded-lg md:border md:border-line/90 md:p-10 md:shadow-panel">
        <div className="mb-5 grid size-14 animate-submit-pop place-items-center rounded-full bg-brand/10 text-brand ring-8 ring-brand/5 md:size-16">
          <span className="block h-4 w-7 rotate-[-45deg] border-b-[4px] border-l-[4px] border-brand" aria-hidden="true" />
        </div>
        <h2 className="m-0 text-[1.72rem] font-extrabold leading-tight tracking-normal md:text-[clamp(1.75rem,2.4vw,2.35rem)]">
          Signup received
        </h2>
        <p className="mt-4 text-muted">
          Your form was saved successfully. A CarFido team member will contact you within 1-2 business days about pilot access and repair partner onboarding.
        </p>
        <ul className="mt-5 grid gap-2.5 text-ink" aria-label="What happens next">
          {nextSteps.map((step) => (
            <li className="flex items-start gap-2.5" key={step}>
              <span className="mt-2.5 block size-2 shrink-0 rounded-full bg-accent" aria-hidden="true" />
              <span>{step}</span>
            </li>
          ))}
        </ul>
        <Link
          className="mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-md bg-brand px-5 py-3 font-extrabold text-white no-underline transition hover:bg-brand-dark focus-visible:bg-brand-dark md:w-auto"
          to="/partner"
        >
          Back to form
        </Link>
      </div>
    </section>
  );
}

export default function RepairPartnersPage() {
  const location = useLocation();
  const confirmation = location.pathname.replace(/\/+$/, '') === '/partner/thanks'
    || location.pathname.replace(/\/+$/, '') === '/signup/thanks'
    || location.pathname.replace(/\/+$/, '') === '/signup/thanks.html';
  const signupSource = getSignupSource(location.search);

  return (
    <BackgroundShell confirmation={confirmation}>
      <Hero confirmation={confirmation} />
      {confirmation ? <ThanksPanel /> : <SignupForm source={signupSource} />}
    </BackgroundShell>
  );
}
